import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Btn, Spinner } from '../../components/ui';
import { adminAPI } from '../../api';

/** Group permissions by the module they belong to, for a readable grid. */
function byModule(permissions) {
  const groups = {};
  for (const p of permissions) {
    const key = p.module || 'other';
    (groups[key] ||= []).push(p);
  }
  return groups;
}

export default function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [open, setOpen]       = useState(null);   // role being edited
  const [draft, setDraft]     = useState([]);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([adminAPI.roles(), adminAPI.permissions()])
      .then(([r, p]) => { setRoles(r.data); setPermissions(p.data); setError(''); })
      .catch(err => {
        console.error(err);
        // Managing roles is a separate permission from managing users.
        setError(err?.response?.status === 403
          ? 'You do not have permission to manage roles.'
          : 'Could not load roles.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openRole = (role) => { setOpen(role); setDraft([...role.permission_codes]); };

  const toggle = (code) =>
    setDraft(d => (d.includes(code) ? d.filter(c => c !== code) : [...d, code]));

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.setRolePerms(open.id, draft);
      toast.success(`${open.name} permissions updated.`);
      setOpen(null);
      load();
    } catch {
      toast.error('Could not update those permissions.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>;
  }
  if (error) return <div className="dash-empty">{error}</div>;

  const groups = byModule(permissions);

  return (
    <>
      <div className="adm-note">
        A role is what an account can do. Changing a role changes it for everyone
        who holds it.
      </div>

      {roles.map(r => (
        <div key={r.id} className="adm-user">
          <div className="adm-user-main">
            <div className="adm-user-name">{r.name}</div>
            <div className="adm-user-sub">{r.code}</div>
            <div className="adm-user-meta">
              {r.permission_codes.length} permission{r.permission_codes.length === 1 ? '' : 's'}
              {' · '}{r.user_count} account{r.user_count === 1 ? '' : 's'}
            </div>
          </div>
          <div className="adm-user-actions">
            <button className="adm-link" onClick={() => openRole(r)}>Permissions</button>
          </div>
        </div>
      ))}

      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(null)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">{open.name}</div>
              <button className="sheet-close" onClick={() => setOpen(null)}>✕</button>
            </div>

            <div className="sheet-body">
              {Object.entries(groups).map(([module, perms]) => (
                <div key={module}>
                  <div className="sheet-lbl">{module}</div>
                  {perms.map(p => (
                    <label key={p.code} className="adm-perm">
                      <input type="checkbox" checked={draft.includes(p.code)}
                             onChange={() => toggle(p.code)} />
                      <span>
                        <span className="adm-perm-name">{p.name}</span>
                        <span className="adm-perm-code">{p.code}</span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>

            <div className="sheet-foot">
              <Btn variant="dark" onClick={save} loading={saving}>Save permissions</Btn>
              <button className="sheet-cancel" onClick={() => setOpen(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
